import { gql } from '@apollo/client'
import { BoxSubscription } from '@jusbrasil-web/shared-apollo-client/__generated__/types'
import { GA4UserProperties, useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { GA4CheckoutCompleteEvent, GA4ClickEvent } from '@jusbrasil-web/shared-ga4-events'
import { useDispatcher } from '@jusbrasil-web/shared-utils-events'
import React, { useCallback } from 'react'

import {
  UseSendOneClickButtonEvents_OfferLockCardFragment,
  UseSendOneClickButtonEvents_ViewerFragment,
} from './__generated__/use-send-one-click-button-events.graphql'

type UseSendOneClickButtonEventsProps = {
  offerLockCard: UseSendOneClickButtonEvents_OfferLockCardFragment
  viewer?: UseSendOneClickButtonEvents_ViewerFragment | null
}

type CurrentSubscription = Pick<BoxSubscription, 'subscriptionId' | 'orderCode'> | null

type UseSendOneClickButtonEventsReturn = {
  sendFailureEvent: () => void
  sendViewEvent: () => void
  metricsRef: React.RefObject<HTMLButtonElement> | undefined
  sendChangeCardEvent: () => void
  sendSuccessEvent: (currentSubscription: CurrentSubscription) => void
  sendSubmittedEvent: () => void
}

const noop = () => undefined

export function useSendOneClickButtonEvents(
  props: UseSendOneClickButtonEventsProps
): UseSendOneClickButtonEventsReturn {
  const { offerLockCard, viewer } = props
  const { sendGA4Event, setGA4UserProperties } = useGA4Dispatcher()
  const { sendMetrics, ref: metricsRef } = useDispatcher<HTMLButtonElement>()
  const hasCreditCard = !!offerLockCard.creditCard?.ccToken

  const { lockEventProperties } = offerLockCard
  const { experiment_id, feature, current_plan, next_plan, from_component, pid, is_upgrade } =
    lockEventProperties

  const isPlanUpgrade = viewer
    ? !!viewer.currentPlan?.orderCode && !viewer.currentPlan?.isLoggedPlan
    : is_upgrade

  const sendArgosEvent = useCallback(
    (eventName: string) => {
      const baseName = `PaymentForm.OneClick${isPlanUpgrade ? '' : 'Buy'}`
      const fullEventName = `${baseName}${eventName}`
      sendMetrics(fullEventName, `${pid}`, {
        source: '', // TODO: implement source. @felipemsantana
        feature,
        next_plan,
        current_plan,
        experiment_id,
        from_component,
      })
    },
    [
      current_plan,
      experiment_id,
      feature,
      isPlanUpgrade,
      from_component,
      next_plan,
      pid,
      sendMetrics,
    ]
  )

  const sendGA4CheckoutCompleteEvent = useCallback(
    (successfulAttempt: boolean) => {
      sendGA4Event(
        new GA4CheckoutCompleteEvent({
          triggerFeature: feature,
          planId: current_plan,
          nextPlanId: next_plan,
          successfulAttempt,
          checkoutMethod: 'one_click',
          paymentMethod: 'credit_card',
        })
      )
    },
    [current_plan, feature, next_plan, sendGA4Event]
  )

  const updateGA4UserProperties = useCallback(
    (currentSubscription: CurrentSubscription) => {
      const { subscriptionId, orderCode } = currentSubscription || {
        subscriptionId: null,
        orderCode: null,
      }
      setGA4UserProperties(new GA4UserProperties({ subscriptionId, orderCode }))
    },
    [setGA4UserProperties]
  )

  const sendChangeCardEvent = useCallback(() => {
    sendGA4Event(new GA4ClickEvent({ componentName: 'change_credit_card_button' }))
    sendArgosEvent('ChangeCard')
  }, [sendArgosEvent, sendGA4Event])

  const sendViewEvent = useCallback(() => {
    sendArgosEvent('View')
  }, [sendArgosEvent])

  const sendFailureEvent = useCallback(() => {
    sendGA4CheckoutCompleteEvent(false)
    sendArgosEvent('Failure')
  }, [sendArgosEvent, sendGA4CheckoutCompleteEvent])

  const sendSuccessEvent = useCallback(
    (currentSubscription: CurrentSubscription) => {
      updateGA4UserProperties(currentSubscription)
      sendGA4CheckoutCompleteEvent(true)
      sendArgosEvent('Success')
    },
    [sendArgosEvent, sendGA4CheckoutCompleteEvent, updateGA4UserProperties]
  )

  const sendSubmittedEvent = useCallback(() => {
    sendArgosEvent('Submitted')
  }, [sendArgosEvent])

  if (hasCreditCard) {
    return {
      metricsRef,
      sendChangeCardEvent,
      sendViewEvent,
      sendFailureEvent,
      sendSuccessEvent,
      sendSubmittedEvent,
    }
  }
  return {
    metricsRef: undefined,
    sendChangeCardEvent: noop,
    sendViewEvent: noop,
    sendFailureEvent: noop,
    sendSuccessEvent: noop,
    sendSubmittedEvent: noop,
  }
}

useSendOneClickButtonEvents.fragments = {
  offerLockCard: gql`
    fragment UseSendOneClickButtonEvents_offerLockCard on OfferLockCard {
      creditCard {
        ccToken
      }
      lockEventProperties {
        pid
        feature
        current_plan
        next_plan
        experiment_id
        modal_type
        app_id
        from_component
        resource_id
        is_upgrade
        is_one_click
      }
    }
  `,
  viewer: gql`
    fragment UseSendOneClickButtonEvents_viewer on Profile {
      currentPlan {
        orderCode
        isLoggedPlan
      }
    }
  `,
}
